/**
 * The console's one QueryClient (main.tsx), built here so its rules are testable.
 *
 * Every write the console makes can move a sidebar "waiting on us" badge — completing a state
 * filing takes one off State filings — so any mutation that succeeds marks `navCountsQuery` stale
 * and the sidebar reads its counts again at once (owner, 2026-11-05: a completed filing stayed on
 * the badge for up to a minute and a half). A failed write moved nothing and leaves them alone.
 */
import { MutationCache, QueryClient } from "@tanstack/react-query";

import { navCountsQuery } from "@/lib/queries-nav-counts";
import { shouldRetryQuery } from "@/lib/query-freshness";

export function createConsoleQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onSuccess: () =>
        client.invalidateQueries({ queryKey: navCountsQuery().queryKey }),
    }),
    defaultOptions: {
      queries: {
        // Admin data is operational: a stale order status is worse than a
        // refetch. Short staleness, refetch on focus, and — through
        // `shouldRetryQuery` — no retry at all on a status the server has
        // already ruled on. `retry: 1` counted failures without reading the
        // error, so a 401/403/404 cost a second request and about a second of
        // delay before the sign-in screen or the error card appeared (A3-15).
        //
        // This is the FLOOR, not the policy. Every read declared in
        // `lib/queries.ts` picks its own window out of `lib/query-freshness.ts`
        // — 15 s for a live list, 30 s for one open record, 60 s and no
        // focus-refetch for an aggregate — because one number for all of them
        // meant a Stripe overview was re-fetched as eagerly as a work queue.
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: shouldRetryQuery,
      },
    },
  });
  return client;
}
